import { useState, useEffect } from 'react';
import { useLanguage } from '../../providers/preferenceProvider';
import { useConfig } from '../../providers/configProvider';
import { useToast } from '../../providers/toastProvider';
import '../../css/overlay.css';
import { createSystemUser, updateSystemUser } from '../../api/system-account-api';
import { deleteUser } from '../../api/user-api';
import { saveUserPreferences } from '../../api/user-preferences-api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIdBadge, faPen, faKey, faPalette, faGlobe, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../providers/preferenceProvider';
import { fetchUserPreferences } from '../../api/user-preferences-api';
import SearchableSelect from './searchableSelect';
import { getRoles } from '../../api/role-api';
import { ACL } from '../../acls';

const EditSystemAccount = ({ isOpen, onClose, systemAccount, createNew, onSystemAccountUpdated }) => {
  const { t, lang: adminLang, getAvailableLanguages } = useLanguage();
  const { isAuthorized } = useConfig();
  const [langs, setLangs] = useState([]);
  const { theme: adminTheme } = useTheme();
  const { showToast, showActionToast, showLoading, hideLoading } = useToast();
  const [fieldErrors, setFieldErrors] = useState([]);

  const [identifier, setIdentifier] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState();
  const [showPassword, setShowPassword] = useState(false);

  const [roleOptions, setRoleOptions] = useState([]);

  const [localLang, setLocalLang] = useState(adminLang);
  const [localTheme, setLocalTheme] = useState(adminTheme);

  useEffect(() => {
    setLangs(getAvailableLanguages());
  }, [getAvailableLanguages]);

  useEffect(() => {
    if (!isOpen) return;
  
    if (systemAccount && systemAccount.id) {
      setIdentifier(systemAccount.email || '');
      setName(systemAccount.firstname || '');
      setPassword('');
      setRole(systemAccount.roleId);
  
      setLocalLang(adminLang);
      setLocalTheme(adminTheme);
  
      const fetchPreferences = async () => {
        try {
          const prefs = await fetchUserPreferences(systemAccount.id);
          setLocalLang(prefs.language || adminLang);
          setLocalTheme(prefs.theme || adminTheme);
        } catch (err) {
          setLocalLang(adminLang);
          setLocalTheme(adminTheme);
        }
      };
  
      fetchPreferences();
    } else {
      setIdentifier('');
      setName('');
      setPassword('');
      setLocalLang(adminLang);
      setLocalTheme(adminTheme);
    }

    fetchRoles();
  }, [isOpen, systemAccount, adminLang, adminTheme]);
  
  async function fetchRoles() {
    setRoleOptions((await getRoles()).data.map((role) => ({
      id: role.id,
      name: role.name
    }))
    );
  }

  if (!isOpen) return null;

  const closeOverlay = () => {
    setIdentifier('');
    setName('');
    setPassword('');
    onClose();
  };

  const saveChange = async () => {
    showLoading();

    const newErrors = [];
    if (identifier.trim() === '') newErrors.push('identifier');
    if (name.trim() === '') newErrors.push('name');
    if (createNew && password.trim() === '') newErrors.push('password');
    if (!role) newErrors.push('role');
    setFieldErrors(newErrors);

    if (newErrors.length > 0) {
      showToast(t.toast.fill_all_fields, 'error');
      hideLoading();
      return;
    }

    try {
      let response;
      if (createNew) {
        response = await createSystemUser(name?.trim(), identifier, password, role);
      } else {
        const pwd = password.trim() !== '' ? password : undefined;
        response = await updateSystemUser(systemAccount.id, name, identifier, pwd, role);
      }

      const userId = response.data?.user?.id || systemAccount?.id;

      await saveUserPreferences(userId, {
        language: localLang,
        theme: localTheme,
      });

      onSystemAccountUpdated?.();
      showToast(
        createNew ? (t.toast.system_account_created) : (t.toast.system_account_updated),
        'success'
      );
      closeOverlay();
    } catch (err) {
      showToast(t.toast.error_occurred, 'error');
    } finally {
      hideLoading();
    }
  };

  const deleteConfirmation = () => {
    showActionToast(
      t.toast.confirm_delete_general.replace('{ph_name}', systemAccount.firstname),
      handleDeleteUser,
      null
    );
  }

  const handleDeleteUser = () => {
    showLoading();
    deleteUser(systemAccount.id).then(() => {
      onSystemAccountUpdated?.();
      hideLoading();
      onClose();
      showToast(t.system_account + " " + t.toast.deleted_success, 'success');
    }).catch(() => {
      hideLoading();
    });
  }

  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-header">
          {createNew ? t.add : t.edit} {t.system_account || "System Account"}
        </div>

        <div className="overlay-admin-form flex flex-col gap-4 p-4">
          <div className="flex flex-row items-center gap-4">
            <label htmlFor="identifier" className="w-32">
              <FontAwesomeIcon icon={faIdBadge} /> {t.identifier}
            </label>
            <input
              type="text"
              id="identifier"
              name="identifier"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={t.identifier + "..."}
              className={"overlay-admin-input" + (fieldErrors.includes("identifier") ? " input-error" : "")}
              required
            />
          </div>

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="name" className="w-32">
              <FontAwesomeIcon icon={faPen} /> {t.name}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.name + "..."}
              className={"overlay-admin-input" + (fieldErrors.includes("name") ? " input-error" : "")}
              required
            />
          </div>

          <div>
            <SearchableSelect label={t.role} icon="role" name="role" value={role} onChange={setRole} options={roleOptions} error={fieldErrors.includes("role")} />
          </div>

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="password" className="w-32">
              <FontAwesomeIcon icon={faKey} /> {t.password}
            </label>
            <div className="relative w-full">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.password + "..."}
                className={"overlay-admin-input" + (fieldErrors.includes("password") ? " input-error" : "")}
                required={createNew}
              />
              <FontAwesomeIcon
                icon={showPassword ? faEyeSlash : faEye}
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 cursor-pointer"
              />
            </div>
          </div>

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="language" className="w-32"><FontAwesomeIcon icon={faGlobe} /> {t.language}</label>
            <select
              id="language"
              value={localLang}
              onChange={(e) => setLocalLang(e.target.value)}
              className="overlay-admin-input"
            >
              {langs.map((code) => (
                <option key={code} value={code}>
                  {t.language_codes?.[code] || code}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="theme" className="w-32"><FontAwesomeIcon icon={faPalette} /> {t.theme}</label>
            <select
              id="theme"
              value={localTheme}
              onChange={(e) => setLocalTheme(e.target.value)}
              className="overlay-admin-input"
            >
              <option value="light">{t.light_mode}</option>
              <option value="dark">{t.dark_mode}</option>
            </select>
          </div>

          <div className="flex flex-row mt-4 gap-2 justify-between">
            <div className='flex flex-row gap-2 align-items-center'>
              {!createNew && (
                <button className="btn" onClick={deleteConfirmation} disabled={!isAuthorized(ACL.USER)}>{t.delete + " " + t.system_account}</button>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <button className="btn-close" onClick={closeOverlay}>{t.close}</button>
              <button className="btn" onClick={saveChange}>{t.confirm}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditSystemAccount;
